import { useParams, Navigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { blogPostsZh, getPostsForRegionZh } from "@/lib/blog-data-zh";
import { ALL_LOCATIONS } from "@/lib/locations";
import { ShareBarEs as ShareBarZh } from "@/components/ShareBarEs"; // reuse the logic
import { Calendar, ArrowRight } from "lucide-react";
import { PricingCalculator } from "@/components/PricingCalculator";
import { BlogLeadCapture } from "@/components/BlogLeadCapture";
import { CostOfSuppliesInfographic } from "@/components/CostOfSuppliesInfographic";
import { MaidServiceChecklistDownload } from "@/components/MaidServiceChecklistDownload";
import { MaidVsHouseCleaningInfographic } from "@/components/MaidVsHouseCleaningInfographic";
import { CleaningHabitTrackerDownload } from "@/components/CleaningHabitTrackerDownload";
import { StandardVsDeepSlider } from "@/components/StandardVsDeepSlider";
import { CleaningFrequencyPlanner } from "@/components/CleaningFrequencyPlanner";
import { BlogFAQ } from "@/components/BlogFAQ";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

const parseChineseDate = (dateString: string): string => {
  try {
    const match = dateString.match(/(\d+)年(\d+)月(\d+)日/);
    if (match) {
      const year = match[1];
      const month = match[2].padStart(2, '0');
      const day = match[3].padStart(2, '0');
      
      const isoString = `${year}-${month}-${day}T00:00:00.000Z`;
      const d = new Date(isoString);
      if (!isNaN(d.getTime())) {
        return d.toISOString();
      }
    }
    
    const fallbackDate = new Date(dateString);
    if (!isNaN(fallbackDate.getTime())) {
      return fallbackDate.toISOString();
    }
    
    return new Date().toISOString();
  } catch (e) {
    return new Date().toISOString();
  }
};

const ChineseBlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  let post = blogPostsZh.find(p => p.slug === slug);
  if (!post) {
    for (const loc of ALL_LOCATIONS) {
      const localPosts = getPostsForRegionZh(loc.name, loc.regionSlug);
      const found = localPosts.find(p => p.slug === slug);
      if (found) {
        post = found;
        break;
      }
    }
  }

  if (!post) {
    return <Navigate to="/zh/blog" replace />;
  }

  const headings: { id: string; title: string }[] = [];
  const processedContent = post.content.replace(/<h2>(.*?)<\/h2>/g, (match, title) => {
    const cleanTitle = title.replace(/<[^>]+>/g, '').trim();
    const id = cleanTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ id, title: cleanTitle });
    return `<h2 id="${id}" class="scroll-m-24">${title}</h2>`;
  });

  return (
    <div className="flex min-h-screen flex-col" lang="zh">
      <SEO 
        title={`${post.title} | Uplevel Cleaning`}
        description={post.excerpt}
        url={`https://uplevelcleaning.com/zh/blog/${post.slug}`}
        alternateUrl={`https://uplevelcleaning.com/blog/${post.slug}`}
        alternateLang="en-US"
        lang="zh"
        articleData={{
          title: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: parseChineseDate(post.date),
          author: post.author
        }}
      />
      <Navbar />
      <main className="flex-1 pt-32 pb-24 relative">
        {showStickyCTA && (
          <div className="fixed top-0 left-0 right-0 z-40 bg-primary text-primary-foreground py-3 px-4 shadow-md animate-in slide-in-from-top-4 fade-in duration-300 md:hidden">
            <div className="container flex items-center justify-between">
              <span className="text-sm font-bold truncate mr-4">准备好迎接洁净的家了吗？</span>
              <Button asChild size="sm" variant="secondary" className="whitespace-nowrap shrink-0">
                <a href="/book?view=custom-plan">立即预约</a>
              </Button>
            </div>
          </div>
        )}
        {showStickyCTA && (
          <div className="fixed top-20 left-0 right-0 z-40 bg-primary text-primary-foreground py-3 px-4 shadow-md animate-in slide-in-from-top-4 fade-in duration-300 hidden md:block">
            <div className="container flex items-center justify-between max-w-4xl">
              <span className="text-base font-bold">准备好迎接洁净的家了吗？60秒获取您的确切报价。</span>
              <Button asChild size="sm" variant="secondary" className="whitespace-nowrap">
                <a href="/book?view=custom-plan">查看报价并预约</a>
              </Button>
            </div>
          </div>
        )}
        <article className="container max-w-4xl">
          <Link to="/zh/blog" className="text-primary hover:underline mb-8 inline-block">
            &larr; 返回博客
          </Link>
          <header className="mb-12 text-center">
            <div className="text-sm text-primary font-bold uppercase tracking-wider mb-4">
              {post.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center justify-center gap-4 text-muted-foreground text-sm">
              <span>{post.date}</span>
              <span>•</span>
              <span>{post.author}</span>
              <span>•</span>
              <span>{post.readTime}</span>
            </div>
          </header>
          <div className="aspect-video w-full rounded-2xl overflow-hidden mb-12 shadow-lg">
            <img src={post.image} alt={post.title} loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
          </div>
          <div className="flex flex-col lg:flex-row gap-12 relative">
            <div className="hidden lg:block w-16 shrink-0">
              <div className="sticky top-32">
                <ShareBarZh url={`https://uplevelcleaning.com/zh/blog/${post.slug}`} title={post.title} variant="sticky-side" />
              </div>
            </div>
            <div className="flex-1">
              {headings.length > 0 && (
                <div className="bg-muted/30 p-6 rounded-2xl mb-12 border border-border">
                  <h3 className="font-bold text-lg mb-4">目录</h3>
                  <ul className="space-y-3">
                    {headings.map(h => (
                      <li key={h.id}>
                        <a href={`#${h.id}`} className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                          {h.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary hover:prose-a:underline" dangerouslySetInnerHTML={{ __html: processedContent }} />
              
              {(post.slug.includes("house-cleaning-cost") || post.slug.includes("pricing") || post.slug.includes("worth-it")) && (
                <CostOfSuppliesInfographic />
              )}

              {post.slug.includes("deep-cleaning-vs-standard-cleaning") && (
                <StandardVsDeepSlider />
              )}

              {post.slug.includes("maid-vs-house-cleaner") && (
                <MaidVsHouseCleaningInfographic />
              )}

              {post.slug.includes("keep-house-clean-between-cleanings") && (
                <CleaningHabitTrackerDownload />
              )}

              {post.slug.includes("how-often-should-you-clean-your-house") && (
                <div className="my-12">
                  <CleaningFrequencyPlanner />
                </div>
              )}

              {post.slug.includes("house-cleaning-cost") && (
                <div className="mt-12">
                  <PricingCalculator />
                  <BlogLeadCapture />
                </div>
              )}

              <BlogFAQ lang="zh" />

              <div className="mt-16 pt-12 border-t">
                <h3 className="text-2xl font-bold mb-8">探索我们的服务</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <Link to="/zh/limpieza-estandar" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                    <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">标准清洁</h4>
                    <p className="text-sm text-muted-foreground">非常适合日常维护，保持家居一尘不染。</p>
                  </Link>
                  <Link to="/zh/limpieza-profunda" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                    <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">深度清洁</h4>
                    <p className="text-sm text-muted-foreground">为需要额外护理的房屋提供从上到下的详细清洁。</p>
                  </Link>
                  <Link to="/zh/limpieza-mudanza" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                    <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">搬家清洁</h4>
                    <p className="text-sm text-muted-foreground">空房子的全面清洁，确保退还押金。</p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Sticky Why Recurring Banner */}
        <div className="sticky bottom-6 mx-auto max-w-3xl z-40 px-4 pb-6 animate-in slide-in-from-bottom-10 fade-in duration-700">
          <Link to="/why-recurring" className="flex items-center justify-between bg-primary text-primary-foreground p-4 md:p-6 rounded-2xl shadow-2xl hover:bg-primary-hover transition-all group border-2 border-white/20">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full hidden sm:block">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg leading-tight">想全年保持家居一尘不染吗？</h4>
                <p className="text-primary-foreground/80 text-sm">了解为什么定期清洁能为您节省高达35%的费用。</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-bold whitespace-nowrap">
              了解更多 <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ChineseBlogPostPage;
